import { useLayoutEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

/**
 * O trecho literal de uma cláusula do contrato, com a página (`TP-054`, T11; `TP-067`). Fechado em três
 * linhas, com "ver trecho inteiro": a citação é a prova, mas a lista não pode virar o contrato inteiro.
 * O botão só aparece quando as três linhas cortaram o texto; trecho curto já está todo ali. Usado na tela de Glosas e na de Contratos, para a mesma regra aparecer do mesmo jeito nas duas.
 */
export function TrechoDoContrato({
  trecho,
  pagina,
  rotulo = 'Cláusula do contrato',
  complemento,
}: {
  trecho: string;
  pagina: number | null;
  /** Nome acessível do bloco. */
  rotulo?: string;
  /** O que vem depois da página na legenda (o número e o título da cláusula, por exemplo). */
  complemento?: string;
}): React.JSX.Element {
  const [inteiro, definirInteiro] = useState(false);
  const [cortado, definirCortado] = useState(false);
  const citacao = useRef<HTMLQuoteElement>(null);

  // Mede com as três linhas aplicadas (fechado) e de novo quando a largura muda.
  useLayoutEffect(() => {
    const elemento = citacao.current;
    if (elemento === null || inteiro) return undefined;
    const medir = (): void => {
      definirCortado(elemento.scrollHeight > elemento.clientHeight + 1);
    };
    medir();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observador = new ResizeObserver(medir);
    observador.observe(elemento);
    return () => {
      observador.disconnect();
    };
  }, [trecho, inteiro]);

  const legenda = [pagina !== null ? `contrato, p. ${String(pagina)}` : null, complemento ?? null]
    .filter((parte): parte is string => parte !== null)
    .join(' · ');

  return (
    <Box sx={(tema) => ({ borderLeft: `3px solid ${tema.palette.divider}`, pl: 1, mt: 0.5 })} aria-label={rotulo}>
      <Typography
        variant="body2"
        component="blockquote"
        ref={citacao}
        sx={{
          m: 0,
          fontStyle: 'italic',
          overflowWrap: 'anywhere',
          ...(inteiro ? {} : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
        }}
      >
        “{trecho}”
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
        {legenda !== '' ? (
          <Typography variant="caption" color="text.secondary">
            {legenda}
          </Typography>
        ) : null}
        {cortado || inteiro ? (
          <Button
            size="small"
            sx={{ minWidth: 0, p: 0 }}
            onClick={() => {
              definirInteiro((atual) => !atual);
            }}
          >
            {inteiro ? 'ver menos' : 'ver trecho inteiro'}
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}
